<template>
  <FairSurveyDialog
    v-model:open="visible"
    :title="configTitle"
    :show-title="isWelcomeStep"
    :exit-label="isWelcomeStep ? 'Назад' : 'Сохранить и выйти'"
    :show-progress="showProgress"
    :progress-percent="progressPercentage"
    :progress-label="progressLabel"
    :loading="tasteModal.isLoadingConfig"
    loading-label="Загрузка опроса…"
    :show-footer="showFooter"
    :show-saved="showSavedIndicator || isTinderLikeStep"
    saved-label="Ответы сохранены"
    :compact="!isDesktop"
    @close="handleHideTasteModal"
  >
    <DynamicStepRenderer />

    <template #actions>
      <!-- Results choice owns its own pair of calls to action. -->
      <template v-if="isResultsChoiceStep">
        <FairButton
          label="Оценить ещё 15 образов"
          variant="tonal"
          size="m"
          :rounded="false"
          @click="handleEvaluateMore"
        />
        <FairButton
          label="Посмотреть результаты"
          variant="primary"
          size="m"
          :rounded="false"
          @click="handleViewResults"
        />
      </template>

      <template v-else-if="currentStep?.type === 'later'">
        <FairButton
          label="Назад"
          variant="tonal"
          size="m"
          :rounded="false"
          @click="handleBackFromLater"
        />
        <FairButton
          label="Перейти в профиль"
          variant="primary"
          size="m"
          :rounded="false"
          @click="handleGoToProfile"
        />
      </template>

      <template v-else>
        <FairButton
          v-if="showLaterButton"
          label="Позже"
          variant="tonal"
          size="m"
          :rounded="false"
          @click="handleLater"
        />
        <FairButton
          v-if="showBackButton"
          label="Назад"
          variant="tonal"
          size="m"
          :rounded="false"
          @click="prevStep"
        />
        <FairButton
          v-if="showNextButton"
          :label="nextButtonLabel"
          variant="primary"
          size="m"
          :rounded="false"
          :disabled="!tasteStore.validateCurrentStep() || tasteModal.isLoading"
          @click="handleNext"
        />
      </template>
    </template>
  </FairSurveyDialog>
</template>

<script setup lang="ts">
import { computed, watch, ref } from "vue";
import { useRouter } from "vue-router";
import { FairSurveyDialog } from "@/ui/patterns/FairSurveyDialog";
import { FairButton } from "@/ui/primitives/FairButton";

import { useTasteClarificationModal } from "@/features/taste/store";
import DynamicStepRenderer from "./TasteSteps/DynamicStepRenderer.vue";
import { useDevice } from "@/composables/useDevice";

const tasteStore = useTasteClarificationModal();
const router = useRouter();
const tasteModal = computed(() => tasteStore.tasteModal);
const currentStep = computed(() => tasteStore.currentStep);
const currentStepIndex = computed(() => tasteStore.tasteModal.currentStepIndex);
const totalSteps = computed(() => tasteStore.totalSteps);
const { isDevice } = useDevice()
const deviceType = computed(() => isDevice.value)
const isDesktop = computed(() => deviceType.value === 'desktop' || deviceType.value === 'smallDesktop')

const visible = ref(false);
const savedSteps = ref<Set<string>>(new Set());
const previousStepId = ref<string | null>(null);

const configTitle = computed(() => {
  return tasteStore.tasteModal.config?.title || "Уточнение вкусов";
});

const isWelcomeStep = computed(() => {
  return currentStep.value?.type === "welcome";
});

const isResultsChoiceStep = computed(() => {
  return currentStep.value?.id === "results-choice";
});


const isStyleEvaluationgChoiceStep = computed(() => {
  return currentStep.value?.id === "style-evaluation-choice" || currentStep.value?.type === "style_evaluation";
});

const isTinderLikeStep = computed(() => {
  return currentStep.value?.type === 'tinder_like';
});

const swipeProgress = computed(() => {
  if (!isTinderLikeStep.value || !currentStep.value) return null;
  const progressKey = `${currentStep.value.id}_sessionProgress`;
  const progress = tasteStore.tasteModal.tasteData[progressKey];
  return progress || null;
});

const progressPercentage = computed(() => {
  // Если это tinder_like шаг и есть данные о свайпах, показываем процент свайпов
  if (isTinderLikeStep.value && swipeProgress.value) {
    if (swipeProgress.value.total_cards === 0) return 0;
    return (swipeProgress.value.swiped_in_session / swipeProgress.value.total_cards) * 100;
  }
  // Иначе показываем обычный прогресс по шагам
  if (totalSteps.value === 0) return 0;
  return ((currentStepIndex.value + 1) / totalSteps.value) * 100;
});

/**
 * Welcome has nothing to measure yet, and the two choice screens are decision
 * points rather than progress — the rail would lie on them.
 */
const showProgress = computed(() => {
  if (!currentStep.value) return false;
  if (isWelcomeStep.value) return false;
  return !isStyleEvaluationgChoiceStep.value && !isResultsChoiceStep.value;
});

/**
 * On the swipe step the counter belongs to the deck, not to the step list:
 * "1 из 5" read as cards on a slow connection.
 */
const progressLabel = computed(() => {
  if (isTinderLikeStep.value) {
    const progress = swipeProgress.value;
    if (progress && progress.total_cards > 0) {
      return `${progress.swiped_in_session} из ${progress.total_cards}`;
    }
    return progress ? "Нет образов" : "Загрузка…";
  }
  return `${currentStepIndex.value + 1} из ${totalSteps.value}`;
});

const showBackButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide back button on welcome step
  if (stepType === "welcome") return false;

  if (stepType === "gender" ) return false;
  
  // Show back button for later step
  if (stepType === "later") return true;
  
  // Hide back button on steps that handle navigation themselves
  if (stepType === "choice" || stepType === "tinder_like" || stepType === "style_evaluation") return false;
  
  return currentStepIndex.value > 0;
});

const showNextButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide next button for results-choice step (buttons are in footer)
  if (isResultsChoiceStep.value) return false;
  
  // Show next button for choice step (as "Начать")
  if (stepType === "choice") return true;
  
  // Hide next button on steps that handle navigation themselves
  if (stepType === "thank_you" || stepType === "tinder_like" || stepType === "later" || stepType === "style_evaluation") {
    return false;
  }
  
  return currentStepIndex.value < totalSteps.value - 1;
});

const showLaterButton = computed(() => {
  if (!currentStep.value) return false;
  // Don't show later button for results-choice step
  if (isResultsChoiceStep.value) return false;
  return currentStep.value.type === "choice";
});

const nextButtonLabel = computed(() => {
  if (!currentStep.value) return "Продолжить";
  
  if (currentStep.value.type === "welcome" || currentStep.value.type === "choice") {
    return "Начать";
  }
  return "Продолжить";
});

const showFooter = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Show footer for results-choice, choice and later steps
  if (isResultsChoiceStep.value || stepType === "choice" || stepType === "later") {
    return true;
  }
  
  // Hide footer on steps that handle navigation themselves
  if (stepType === "thank_you") {
    return false;
  }
  
  return true;
});

const showSavedIndicator = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const stepId = currentStep.value.id;
  
  // Не показываем плашку для исключенных шагов
  if (stepType === "welcome" || stepType === "later" || stepType === "thank_you" || stepType === "tinder_like") {
    return false;
  }
  
  // Показываем если есть данные для текущего шага и шаг в списке сохраненных
  const hasData = tasteStore.tasteModal.tasteData[stepId] !== undefined && 
                  tasteStore.tasteModal.tasteData[stepId] !== null &&
                  (Array.isArray(tasteStore.tasteModal.tasteData[stepId]) 
                    ? tasteStore.tasteModal.tasteData[stepId].length > 0
                    : true);
  
  return hasData && savedSteps.value.has(stepId);
});

function handleHideTasteModal() {
  tasteStore.hideTasteModal();
}

function prevStep() {
  tasteStore.prevStep();
}

function handleNext() {
  if (!tasteStore.validateCurrentStep()) {
    return;
  }
  
  tasteStore.nextStep();
}

function handleLater() {
  tasteStore.showLaterStep();
}

function handleGoToProfile() {
  tasteStore.hideTasteModal();
  router.push('/profile');
}

function handleBackFromLater() {
  tasteStore.hideLaterStep();
}

function handleEvaluateMore() {
  // Сохраняем выбор "evaluate_more" для валидации шага
  if (currentStep.value) {
    tasteStore.updateTasteData(currentStep.value.id, 'evaluate_more');
  }
  tasteStore.nextStep();
}

function handleViewResults() {
  tasteStore.hideTasteModal();
  router.push('/profile/my-style');
}

watch(
  () => tasteModal.value.isShow,
  (newVal) => {
    visible.value = newVal;
    if (!newVal) {
      // Сбрасываем состояние при закрытии модального окна
      savedSteps.value.clear();
      previousStepId.value = null;
    }
  }
);

// Отслеживаем изменения в tasteData и добавляем ID шага в список сохраненных
watch(
  () => tasteStore.tasteModal.tasteData,
  (newData) => {
    if (!currentStep.value) return;
    const stepId = currentStep.value.id;
    const stepType = currentStep.value.type;
    
    // Не отслеживаем исключенные шаги
    if (stepType === "welcome" || stepType === "later" || stepType === "thank_you" || stepType === "tinder_like") {
      return;
    }
    
    // Проверяем, есть ли данные для текущего шага
    const hasData = newData[stepId] !== undefined && 
                    newData[stepId] !== null &&
                    (Array.isArray(newData[stepId]) 
                      ? newData[stepId].length > 0
                      : true);
    
    if (hasData) {
      savedSteps.value.add(stepId);
    }
  },
  { deep: true }
);

// Отслеживаем смену шага и проверяем, есть ли уже данные для нового шага
watch(
  () => currentStep.value?.id,
  (newStepId) => {
    if (!newStepId || !currentStep.value) return;
    
    const stepType = currentStep.value.type;
    
    // Не обрабатываем исключенные шаги
    if (stepType === "welcome" || stepType === "later" || stepType === "thank_you" || stepType === "tinder_like") {
      previousStepId.value = newStepId;
      return;
    }
    
    // Проверяем, есть ли уже данные для этого шага
    const stepData = tasteStore.tasteModal.tasteData[newStepId];
    const hasData = stepData !== undefined && 
                    stepData !== null &&
                    (Array.isArray(stepData) ? stepData.length > 0 : true);
    
    if (hasData) {
      savedSteps.value.add(newStepId);
    }
    
    previousStepId.value = newStepId;
  }
);
</script>
